<template>
  <div class="chart-wrapper" style="position: relative;">
    <ChartDownloadButton
      :title="chartTitle"
      :get-chart="getChartInstance"
      :disabled="loading"
    />
    <div class="chart-title">{{ chartTitle }}</div>
    <ChartSpinner :visible="loading" :show-watermark="false" />
    <v-chart
      ref="chartRef"
      v-if="!loading && chartOption.series && chartOption.series.length"
      :option="chartOption"
      :style="{ width: '100%', height: chartHeight + 'px' }"
    />
  </div>
</template>

<script>
import { defineComponent, ref, watch, computed } from 'vue';
import { useResponsiveCharts } from '@/utils/useResponsiveCharts.js';
import VChart from 'vue-echarts';
import ChartSpinner from '@/components/common/ChartSpinner.vue';
import ChartDownloadButton from '@/components/dashboard/charts/ChartDownloadButton.vue';
import chartColors from '@/utils/chartColors.js';
import { appendWatermark } from '@/utils/chartWatermark';

/**
 * 插入换行符
 * @param {string} str
 * @param {number} charsPerLine
 * @returns {string}
 */
function wordWrap(str, charsPerLine) {
  if (!str) return '';
  const regex = new RegExp(`.{1,${charsPerLine}}`, 'g');
  const lines = str.match(regex);
  return lines ? lines.join('\n') : str;
}

/**
 * 计算 y 轴的最大值和间隔
 * @param {Array} dataArray - 数据数组
 * @param {number} baseMultiple - 基础倍数（bar 为 60，line 为 6）
 * @param {number} groups - 分组数量，默认为 6
 * @returns {{max: number, interval: number}}
 */
function calculateYAxisConfig(dataArray, baseMultiple, groups = 6) {
  if (!dataArray || dataArray.length === 0) {
    return { max: baseMultiple * groups, interval: baseMultiple };
  }

  // 过滤并转换有效数据
  const validData = dataArray
    .filter(v => v != null && !isNaN(parseFloat(v)))
    .map(v => parseFloat(v));

  // 如果没有有效数据，返回默认值
  if (validData.length === 0) {
    return { max: baseMultiple * groups, interval: baseMultiple };
  }

  // 找到所有数据中的最大值
  const maxValue = Math.max(...validData);

  if (maxValue <= 0 || !isFinite(maxValue)) {
    return { max: baseMultiple * groups, interval: baseMultiple };
  }

  // 向上取整到 baseMultiple 的倍数
  const roundedMax = Math.ceil(maxValue / baseMultiple) * baseMultiple;
  
  // 确保至少有一个分组，如果最大值很小，至少显示 baseMultiple
  const finalMax = Math.max(roundedMax, baseMultiple);
  
  // 计算间隔，确保能分成 groups 组
  const interval = finalMax / groups;

  return { max: finalMax, interval };
}

export default defineComponent({
  name: 'ComboBarLineChart',
  components: {
    VChart,
    ChartSpinner,
    ChartDownloadButton
  },
  props: {
    chartTitle: { type: String, default: '柱状图 + 折线图' },
    categories: { type: Array, default: () => [] },
    barSeries: { type: Array, default: () => [] },
    lineSeries: { type: Object, default: () => ({ name: '折线', data: [] }) },
    yAxisBarName: { type: String, default: '数量（个）' },
    yAxisLineName: { type: String, default: '金额（亿元）' },
    yAxisBarMax: { type: Number, default: null }, // null 表示自动计算
    yAxisBarInterval: { type: Number, default: null }, // null 表示自动计算
    yAxisLineMax: { type: Number, default: null }, // null 表示自动计算
    yAxisLineInterval: { type: Number, default: null }, // null 表示自动计算
    chartAnnotation: { type: String, default: '' },
    chartHeight: { type: Number, default: 440 }
  },
  setup(props) {
    const { isMobile } = useResponsiveCharts();
    const chartOption = ref({});
    const loading = ref(false);
    const chartRef = ref(null);
    const getChartInstance = () => {
      const vm = chartRef.value;
      if (!vm) return null;
      if (vm.chart && typeof vm.chart.getDom === 'function') return vm.chart;
      return vm;
    };

    const updateChart = () => {
      loading.value = true;
      setTimeout(() => {
        // 计算 bar 数据的 y 轴配置
        const allBarData = (props.barSeries || []).flatMap(s => s.data || []);
        const barConfig = props.yAxisBarMax != null && props.yAxisBarInterval != null
          ? { max: props.yAxisBarMax, interval: props.yAxisBarInterval }
          : calculateYAxisConfig(allBarData, 60, 6);

        // 计算 line 数据的 y 轴配置
        const lineData = props.lineSeries?.data || [];
        const lineConfig = props.yAxisLineMax != null && props.yAxisLineInterval != null
          ? { max: props.yAxisLineMax, interval: props.yAxisLineInterval }
          : calculateYAxisConfig(lineData, 6, 6);

        const gridConfig = {
          left: '3%',
          right: '4%',
          bottom: isMobile.value ? '20%' : '60px',
          containLabel: true
        };
        const xAxisLabelConfig = {
          fontSize: isMobile.value ? 9 : 11,
          interval: 0,
          // formatter: val => (val.length > 6 ? val.slice(0, 6) + '…' : val),
          align: 'center'
        };

        const yAxisBarNameFormatted = computed(() => {
          if (isMobile.value) {
            return props.yAxisBarName;//.replace('数量', '\n数量')
          }
          return props.yAxisBarName;
        });
        const yAxisLineNameFormatted = computed(() => {
          if (isMobile.value) {
            return props.yAxisLineName;//.replace('金额', '\n金额');
          }
          return props.yAxisLineName;
        });
        const yAxisNameGap = isMobile.value ? 10 : 45;
        const yAxisNameTextStyle = {
            fontSize: isMobile.value ? 10 : 14,
            padding: isMobile.value ? [0, 0, 0, 40] : [0, 0, 0, 0] // 左Y轴标题向左偏移
        };
        const yAxisNameTextStyleRight = {
            fontSize: isMobile.value ? 10 : 14,
            padding: isMobile.value ? [0, 0, 0, -40] : [0, 0, 0, 0] // 右Y轴标题向右偏移
        };

        const annotationBottom = isMobile.value ? '8%' : 25;
        const annotationWidth = isMobile.value ? '70%' : '100%';

        const barLabelOption = {
          show: true,
          position: 'top',
          formatter: '{c}',
          fontSize: isMobile.value ? 10 : 12,
          color: '#005f73',
        };

        const bar = (props.barSeries || []).map((s, idx) => ({
          name: s.name,
          type: 'bar',
          data: s.data || [],
          yAxisIndex: 0,
          itemStyle: {
            color: chartColors[idx % chartColors.length]
          },
          label: barLabelOption
        }));

        const customLineColor = '#ee9b00';
        const actualLineSeriesName = props.lineSeries?.name || '折线';

        const lineLabelOption = {
          show: true,
          position: isMobile.value ? 'left' : 'right', 
          offset: isMobile.value ? [10, -10] : [3, -25],
          distance: 8,
          formatter: function (params) {
            if (params.value == null || isNaN(parseFloat(params.value))) { 
              return '';
            }
            // 转换为数字并格式化
            const value = parseFloat(params.value);
            return value.toFixed(2);
          },
          fontSize: isMobile.value ? 10 : 12,
          color: '#ee9b00',
        };

        const line = {
          name: actualLineSeriesName,
          type: 'line',
          data: props.lineSeries?.data || [],
          yAxisIndex: 1,
          symbol: 'circle',
          lineStyle: {
            width: 3,
            color: customLineColor 
          },
          itemStyle: {
            color: customLineColor
          },
          label: lineLabelOption
        };

        chartOption.value = {
          color: chartColors,
          tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'shadow' },
            formatter: function (params) {
              let tooltipContent = '';
              if (params && params.length > 0) {
                tooltipContent += (params[0].axisValueLabel || params[0].name) + '<br />'; 
                params.forEach(param => {
                  let valueToShow = param.value;
                  if (param.seriesName === actualLineSeriesName) {
                    const num = parseFloat(param.value);
                    if (!isNaN(num)) valueToShow = num.toFixed(2);
                    else if (param.value == null) valueToShow = '-';
                    else valueToShow = String(param.value);
                  } else {
                    const num = parseFloat(param.value);
                    if (!isNaN(num)) valueToShow = num.toFixed(0);
                    else if (param.value == null) valueToShow = '—';
                    else valueToShow = String(param.value);
                  }
                  tooltipContent += param.marker + ' ' + param.seriesName + ': <b>' + valueToShow + '</b><br />';
                });
              }
              return tooltipContent;
            }
          },
          legend: { top: 10 },

          grid: gridConfig,

          xAxis: {
            type: 'category',
            data: props.categories || [],
            axisLabel: xAxisLabelConfig
          },
          yAxis: [
            {
              type: 'value', 
              name: yAxisBarNameFormatted.value, 
              min: 0, max: barConfig.max,
              interval: barConfig.interval, 
              nameGap: yAxisNameGap,
              nameLocation: isMobile.value ? 'end' : 'middle',
              nameTextStyle: yAxisNameTextStyle,
              axisLabel: {
                fontSize: isMobile.value ? 9 : 12
              }
            },
            {
              type: 'value', 
              name: yAxisLineNameFormatted.value, 
              min: 0, max: lineConfig.max,
              interval: lineConfig.interval, 
              nameGap: yAxisNameGap,
              nameLocation: isMobile.value ? 'end' : 'middle',
              nameTextStyle: yAxisNameTextStyleRight,
              axisLabel: {
                fontSize: isMobile.value ? 9 : 12
              }
            }
          ],
          series: [...bar, line]
        };

        if (props.chartAnnotation) {
          const charsPerLine = isMobile.value ? 30 : 60;
          const wrappedAnnotationText = wordWrap(props.chartAnnotation, charsPerLine);

          chartOption.value.graphic = {
            type: 'text',
            left: 'center',
            bottom: annotationBottom,
            style: {
              text: wrappedAnnotationText,
              fill: '#BDA36C',
              fontSize: isMobile.value ? 9 : 12,
              textAlign: 'center',
              lineHeight: isMobile.value ? 12 : 18,
              width: annotationWidth 
            }
          };
        }

        appendWatermark(chartOption.value);
        loading.value = false;
      }, 500);
    };

    watch(
      () => [props.barSeries, props.lineSeries, props.categories],
      updateChart,
      { immediate: true, deep: true }
    );
    
    watch(isMobile, updateChart);

    return {
      chartOption,
      loading,
      chartRef,
      getChartInstance
    };
  }
});
</script>

<style scoped>
.chart-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 90%;
  min-width: 0px;
  margin: 20px auto; 
}

.chart-title {
  font-size: 18px;
  font-weight: bold;
  margin: 20px 0;
  text-align: center;
  color: #2e3968;
}

</style>
